/* eslint-env browser */
import debounce from "lodash/debounce";
import { Controller } from "stimulus";
import useStatusClasses from "../../composables/use_status_classes";
import useEventController from "../../composables/use_event_controller";

export default class extends Controller {
  static targets = ["allocation", "cardCheckbox", "rowCheckbox", "progressBar", "progressIndicator", "receivableInput"];

  static values = {
    status: { type: String, default: "need_to_receive" },
    allocation: { type: Number, default: 0 },
    initialAllocation: { type: Number, default: 0 },
    maxAllocation: { type: Number, default: 0 },
  };

  updateTotals = debounce(this.#updateTotals.bind(this), 10);

  connect() {
    useEventController(this);
    this.listenOn(document, "receivingReset", this.reset.bind(this));
    this.listenOn(document, "receivingRefresh", this.refresh.bind(this));
    this.listenOn(document, "syncStatusIndicator", this.syncStatusIndicator.bind(this));
    this.listenOn(this.element, "focusin", this.#buildControllers.bind(this));

    useStatusClasses(this, "card");

    Object.assign(this, {
      initialAllocationValueChanged: this.#initialAllocationUpdated.bind(this),
      statusValueChanged: this.#statusUpdated.bind(this),
    });

    this.statusValueChanged(this.statusValue, undefined);
    this.progressBarTarget.dataset.indicates = this.indicatorStatus;
  }

  allocationValueChanged(value) {
    this.allocationTarget.textContent = value;
    this.progressIndicatorTarget.style.width = `${this.selectedPercentage}%`;
  }

  refresh() {
    this.initialAllocationValue = this.allocationValue;
    this.resetCheckboxes();
  }

  reset() {
    this.allocationValue = this.initialAllocationValue;
    this.resetCheckboxes();
  }

  resetCheckboxes() {
    this.cardCheckboxTarget.indeterminate = false;
    this.cardCheckboxTarget.checked = false;

    this.rowCheckboxTargets.forEach((checkbox) => {
      checkbox.indeterminate = false;
      checkbox.checked = false;
    });
  }

  syncStatusIndicator() {
    this.progressBarTarget.dataset.indicates = this.indicatorStatus;
  }

  toggleCardCheckbox(event) {
    this.element.classList.add("pointer-events-none"); // This can take a second on larger PO's prevent user from trying again
    this.element.querySelectorAll("[data-component='groupRowCheckbox']").forEach((checkbox) => {
      checkbox.indeterminate = false;
      checkbox.checked = event.target.checked;
      checkbox.dispatchEvent(new Event("change"));
    });
    setTimeout(() => {
      this.element.classList.remove("pointer-events-none");
    });
  }

  toggleRowCheckbox(event) {
    const row = event.target.closest("[data-component=groupRow]");
    this.dispatch(`receivingRowToggleRow:${row.dataset.domId}`, {
      prefix: false,
      detail: { checked: event.target.checked },
    });
  }

  #updateTotals() {
    this.allocationValue = this.receivableInputTargets.reduce(
      (value, receivable) => value + parseInt(receivable.value, 10),
      0,
    );
  }

  get selectedPercentage() {
    return Math.min(100, (this.allocationValue / this.maxAllocationValue) * 100);
  }

  get indicatorStatus() {
    if (!this.cardCheckboxTarget.indeterminate && this.selectedPercentage >= 100) {
      return "full";
    }

    if (this.selectedPercentage) {
      return "partial";
    }

    return "empty";
  }

  #initialAllocationUpdated(value) {
    const allReceived = this.receivableInputTargets.every(
      (input) => parseInt(input.value, 10) >= parseInt(input.dataset.max, 10),
    );

    if (allReceived) {
      this.statusValue = "received";
    } else if (value > 0) {
      this.statusValue = "partially_received";
    } else {
      this.statusValue = "need_to_receive";
    }
  }

  #statusUpdated(value, previousValue) {
    this.element.dataset.status = value;
    this.updateStatusClasses(value, previousValue);
  }

  #buildControllers() {
    const missing = Array.from(this.element.querySelectorAll("[data-controller-delay]"));

    missing.forEach((element) => {
      const { controllerDelay, controller } = element.dataset;
      if (controller !== controllerDelay) {
        element.dataset.controller = controllerDelay;
      }
    });
  }
}
